import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
} from "@mui/material";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { createLink, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import dayjs from "dayjs";
import { times } from "lodash-es";
import { useId, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslations } from "use-intl";

import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { IconTripsError } from "@/components/Icons";
import { CalendarDrawer } from "@/features/trips/components/bookingFlow/booking/CalendarDrawer";
import {
  tripAvailabilityQueryOptions,
  useTripEffectiveStart,
} from "@/features/trips/hooks/useTripAvailability";
import type { TripDetails } from "@/features/trips/types/types";

const ButtonLink = createLink(Button);

type DateButtonProps = {
  date: string;
  isPressed: boolean;
  onClick: () => void;
};

function DateButton({ date, isPressed, onClick }: DateButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={isPressed}
      onClick={onClick}
      className={clsx(
        "m-px flex flex-col items-center gap-1 rounded-xl border border-stroke-neutral-soft px-2 py-3 font-medium transition-colors",
        "aria-pressed:m-0 aria-pressed:border-2 aria-pressed:border-core-accent-500 aria-pressed:bg-surface-accent-softer",
      )}
    >
      <span className="min-w-12 text-sm text-nowrap text-text-primary">
        {dayjs(date).format("D MMM")}
      </span>
      <span className="text-xs text-text-placeholder">
        {dayjs(date).format("ddd")}
      </span>
    </button>
  );
}

function DateButtonSkeleton() {
  return (
    <div className="m-px flex flex-col items-center gap-1 rounded-xl border border-stroke-neutral-soft px-2 py-3 font-medium">
      <Skeleton>
        <span className="block min-w-12 text-sm text-nowrap">
          {dayjs().format("D MMM")}
        </span>
      </Skeleton>
      <Skeleton>
        <span className="block text-xs">{dayjs().format("ddd")}</span>
      </Skeleton>
    </div>
  );
}

type TripDateSelectionCardProps = {
  trip: TripDetails;
  selectedDate: string | null;
  onChange: (selectedDate: string) => void;
};

export function TripDateSelectionCard({
  trip,
  selectedDate,
  onChange,
}: TripDateSelectionCardProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const groupId = useId();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const { effectiveStartMonth, allPossibleMonths, isEffectiveStartLoading } =
    useTripEffectiveStart(trip);

  const availableMonths = useMemo(() => {
    if (!effectiveStartMonth) return allPossibleMonths;

    const startIndex = allPossibleMonths.indexOf(effectiveStartMonth);
    if (startIndex === -1) return allPossibleMonths;

    return allPossibleMonths.slice(startIndex);
  }, [allPossibleMonths, effectiveStartMonth]);

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
  const selectedMonth = availableMonths[selectedMonthIndex];

  const { data: availabilityData, isPending: isLoadingAvailability } = useQuery(
    {
      ...tripAvailabilityQueryOptions(trip.id, selectedMonth),
      enabled: !isEffectiveStartLoading,
    },
  );

  const isPending = isEffectiveStartLoading || isLoadingAvailability;
  const isNoDatesAvailable =
    !isEffectiveStartLoading && effectiveStartMonth === null;

  const buttonStyles = {
    color: "var(--color-text-interactive)",
    "&.Mui-disabled": {
      color: "var(--color-text-disabled-interactive)",
    },
  };

  return (
    <div className="rounded-3xl bg-white p-4">
      <div className="-mt-2 mb-2 flex items-center justify-between gap-2 text-text-primary">
        <IconButton
          onClick={() => {
            setSelectedMonthIndex((prev) => prev - 1);
          }}
          disabled={
            isEffectiveStartLoading ||
            dayjs(selectedMonth).isSame(availableMonths[0], "month")
          }
          type="button"
          aria-label={t("trips.dateSelection.previousMonthLabel")}
          sx={buttonStyles}
        >
          <ArrowLeftIcon size={24} weight="bold" className="rtl:-scale-x-100" />
        </IconButton>

        <div className="text-sm font-bold">
          {isPending ? (
            <Skeleton className="min-w-36" />
          ) : (
            <>{dayjs(selectedMonth).format("MMMM YYYY")}</>
          )}
        </div>

        <IconButton
          onClick={() => {
            setSelectedMonthIndex((prev) => prev + 1);
          }}
          disabled={
            isEffectiveStartLoading ||
            dayjs(selectedMonth).isSame(
              availableMonths[availableMonths.length - 1],
              "month",
            )
          }
          type="button"
          aria-label={t("trips.dateSelection.nextMonthLabel")}
          sx={buttonStyles}
        >
          <ArrowRightIcon
            size={24}
            weight="bold"
            className="rtl:-scale-x-100"
          />
        </IconButton>
      </div>

      <div
        id={groupId}
        role="group"
        aria-label={t("trips.dateSelection.ariaLabel")}
        className="-mx-4 flex snap-x snap-mandatory flex-nowrap gap-3 overflow-x-auto px-4"
      >
        {isPending
          ? times(4, (i) => <DateButtonSkeleton key={i} />)
          : availabilityData
              ?.filter(({ date }) => dayjs(date).isSame(selectedMonth, "month"))
              .map(({ date }) => (
                <DateButton
                  key={date}
                  date={date}
                  isPressed={date === selectedDate}
                  onClick={() => {
                    onChange(date);
                  }}
                />
              ))}
      </div>

      <div className="mt-2 flex justify-center">
        <Button
          variant="text"
          disabled={isEffectiveStartLoading}
          onClick={(e) => {
            setDrawerOpen(true);
            e.currentTarget.blur();
          }}
        >
          {t("trips.dateSelection.seeAllDates")}
        </Button>
      </div>

      <CalendarDrawer
        tripId={trip.id}
        selectedDate={selectedDate}
        startMonth={availableMonths[0]}
        endMonth={availableMonths[availableMonths.length - 1]}
        selectedMonth={selectedMonth}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onConfirm={(tripDate) => {
          if (tripDate) {
            // update state synchronously to be able to scroll to the selected date button
            flushSync(() => {
              setSelectedMonthIndex(
                availableMonths.indexOf(dayjs(tripDate).format("YYYY-MM")),
              );
              onChange(tripDate);
            });

            document
              .querySelector(`#${groupId} button[aria-pressed="true"]`)
              ?.scrollIntoView({ behavior: "smooth", inline: "center" });
          }
          setDrawerOpen(false);
        }}
      />

      <Dialog
        open={isNoDatesAvailable}
        fullWidth
        maxWidth="xs"
        onClose={() => {
          navigate({ to: "/trips/search" });
        }}
      >
        <DialogContent>
          <ErrorPanel
            icon={IconTripsError}
            primaryText={t("trips.bookingPage.notAvailable.title")}
            secondaryText={t("trips.bookingPage.notAvailable.description")}
          >
            <ButtonLink to="/trips/search" variant="outlined">
              {t("back")}
            </ButtonLink>
          </ErrorPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
